import { versionlessSchemaCollectionRemoved } from '@wix/bi-logger-platform-cm/v2'
import {
  openGenericConfirmationPanel,
  getReferringSchemas,
} from '@wix/wix-data-client-common'
import {
  countConnectedDynamicPages,
  countReferencesToCollection,
} from '@wix/wix-data-client-common-standalone'
import { findPresetByPermissions } from '@wix/wix-code-collections-api'
import { MULTILINGUAL_PLUGIN_ID } from '@wix/wix-data-schema-types'
import { getCMSWebRemoveCollectionPanelUrl } from './cms-web-panels'

const getBiLoggerWithDefaults = ({
  allSchemas,
  schema,
  collectionId,
  permissions,
  fields,
  logger,
  origin,
  eventCreator,
}) => {
  const referringSchemas = getReferringSchemas({
    allSchemas,
    referencedSchema: schema,
  })

  const numberOfReferringFields = countReferencesToCollection(
    collectionId,
    referringSchemas,
  )

  const numberOfConnectedDynamicPages = countConnectedDynamicPages({
    fields,
  })

  const collectionType = findPresetByPermissions(permissions)

  return category =>
    logger.bi(
      eventCreator({
        collection_id: collectionId,
        collection_name: schema.displayName,
        collection_type: collectionType,
        category,
        num_of_connected_dynamic_pages: numberOfConnectedDynamicPages,
        num_of_reference_fields_to_collection: numberOfReferringFields,
        origin,
      }),
    )
}

export default async ({
  editorSdkProxy,
  appApi,
  collectionsApi,
  logger,
  i18n,
  collectionId,
  hostname,
  origin,
}) => {
  const t = i18n.t.bind(i18n)
  const allSchemas = await collectionsApi.getAll()

  const schema = allSchemas.find(({ id }) => id === collectionId)

  const { fields, permissions, plugins } = schema

  const logVersionlessSchemaBiEvent = getBiLoggerWithDefaults({
    allSchemas,
    schema,
    collectionId,
    permissions,
    fields,
    logger,
    origin,
    eventCreator: versionlessSchemaCollectionRemoved,
  })

  const removeCollection = async () => {
    try {
      await appApi.removeCollection({ collectionId })
      await editorSdkProxy.document.application.livePreview.refresh({
        source: 'AFTER_DB_DELETION',
        shouldFetchData: true,
      })

      await editorSdkProxy.editor.routers.refresh()

      appApi.refreshCollectionItemCounts([collectionId])
      logVersionlessSchemaBiEvent('remove')
    } catch (error) {
      logger.error(
        new Error(`Failed to remove a collection`, { cause: error }),
        { collectionId },
      )
      openGenericConfirmationPanel({
        editorSdkProxy,
        logger,
        t,
      })
    }
  }

  const isMultilingualEnabled = Boolean(plugins?.[MULTILINGUAL_PLUGIN_ID])

  const url = await getCMSWebRemoveCollectionPanelUrl({
    hostname,
    editorSdkProxy,
  })

  const panelResult = await editorSdkProxy.editor.openModalPanel({
    url,
    initialData: {
      isMultilingualCollection: isMultilingualEnabled,
    },
    shouldHideHeader: true,
    width: 510,
    height: 212,
  })

  if (panelResult) {
    const collection = await collectionsApi.get(collectionId)

    if (!collection || collection.isDeleted) {
      logVersionlessSchemaBiEvent('remove')
      return
    }

    removeCollection()
  } else {
    logVersionlessSchemaBiEvent('close')
  }
}
